import { DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Select, Tooltip, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { ModelProduct } from '@app/api/Products';
import {
  useDeleteClientServiceProductUsagesMutation,
  useSaveClientServiceProductUsagesMutation,
} from '@app/api/TechnologicalMaps';
import { SUCCESS } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { MaskedInputNumber } from '@app/pages/Clients/EditClientService/components/ClientServiceProductUsages/components';
import type {
  TableRow,
  UseClientServiceProductUsagesProps,
} from '@app/pages/Clients/EditClientService/components/ClientServiceProductUsages/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

import type { DefaultOptionType } from 'antd/es/select';

const { Text } = Typography;

export const useClientServiceProductUsages = ({
  warehousesData,
  clientServiceProductUsagesData,
  productsData,
  serviceId,
}: UseClientServiceProductUsagesProps) => {
  const [rows, setRows] = useState<TableRow[]>([]);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<ModelProduct[]>([]);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);

  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const [query] = useSearchParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryServiceId = useMemo(() => Number(query.get('id')), [query]);
  const memoizedServiceId = useMemo(
    () => serviceId || queryServiceId,
    [queryServiceId, serviceId]
  );
  const copyingService = useMemo(() => query.get('copyingService'), [query]);

  const [
    saveClientServiceProductUsages,
    { isLoading: isSaveClientServiceProductUsagesLoading },
  ] = useSaveClientServiceProductUsagesMutation();
  const [
    DeleteClientServiceProductUsages,
    { isLoading: isDeleteClientServiceProductUsagesLoading },
  ] = useDeleteClientServiceProductUsagesMutation();

  useEffect(() => {
    const rowsData = (clientServiceProductUsagesData || []).map(
      ({ ProductId, Product, WarehouseId, UsageAmount, Id }) => ({
        id: Id,
        productId: ProductId,
        name: Product,
        warehouseId: WarehouseId,
        amount: UsageAmount,
      })
    );

    setRows(rowsData);
  }, [clientServiceProductUsagesData]);

  useEffect(() => {
    const serverDataProductsIds = (clientServiceProductUsagesData || []).map(
      ({ ProductId }) => ProductId
    );

    if (serverDataProductsIds) {
      const selectedProducts = (productsData || []).filter((product) =>
        serverDataProductsIds.includes(product.Id)
      );

      setSelectedProducts(selectedProducts);
    }
  }, [clientServiceProductUsagesData, productsData]);

  const onAdd = (newSelectedProducts: ModelProduct[]) => {
    setRows((prevRows) => {
      const preservedRows = [...prevRows];

      const existingIds = new Set(prevRows.map((row) => row.productId));

      const newProducts = newSelectedProducts.filter(
        (p) => !existingIds.has(p.Id)
      );

      const adaptedNewRows: TableRow[] = newProducts.map(({ Name, Id }) => ({
        id: 0,
        productId: Id,
        name: Name || '',
        warehouseId: null,
        amount: 0,
      }));

      return [...preservedRows, ...adaptedNewRows];
    });

    setSelectedProducts((prevSelected) => {
      const updatedSelected = [...prevSelected];

      newSelectedProducts.forEach((newProduct) => {
        const exists = updatedSelected.some((p) => p.Id === newProduct.Id);
        if (!exists) {
          updatedSelected.push(newProduct);
        }
      });

      return updatedSelected;
    });

    handleProductModalClose();
  };

  const updateRow = (
    productId: number,
    key: keyof TableRow,
    value: number | string
  ) => {
    setRows((prev) =>
      prev.map((row) =>
        row.productId === productId ? { ...row, [key]: value } : row
      )
    );
  };

  const deleteRow = (productId: number) => {
    setRows((prev) => prev.filter((row) => row.productId !== productId));
    setSelectedProducts((prev) => prev.filter((p) => p.Id !== productId));
  };

  const warehousesOptions: DefaultOptionType[] = useMemo(
    () =>
      warehousesData?.map(({ Name, Id }) => ({
        label: Name,
        value: Id,
      })) || [],
    [warehousesData]
  );

  const columns = [
    {
      title: 'Расходник, объём',
      dataIndex: 'name',
      width: '45%',
      render: (_: string, row: TableRow) => <Text>{row.name}</Text>,
    },
    {
      title: 'Склад',
      dataIndex: 'warehouse',
      width: '25%',
      render: (_: string, row: TableRow) => (
        <Select
          value={row.warehouseId}
          options={warehousesOptions}
          onChange={(value) => updateRow(row.productId, 'warehouseId', value)}
          className="warehouse-selector"
          disabled={Boolean(serviceId)}
          showSearch
          optionFilterProp="label"
        />
      ),
    },
    {
      title: (
        <>
          <Text>Пропорция</Text>{' '}
          <Tooltip
            title={
              <span>
                Доля товара, используемая по технологической карте:
                <br />- Если товар используется полностью: 1/1
                <br />- Если используется половина товара: 1/2
              </span>
            }
          >
            <InfoCircleOutlined />
          </Tooltip>
        </>
      ),
      dataIndex: 'amount',
      width: '20%',
      render: (_: string, row: TableRow) => (
        <MaskedInputNumber
          value={row.amount}
          isAllowed={(v) => v >= 0.0001 && v <= 1}
          onSuccess={({ value }) => {
            updateRow(row.productId, 'amount', value);
          }}
          onChange={(v) => {
            updateRow(row.productId, 'amount', v ?? '');
          }}
          disabled={Boolean(serviceId)}
        />
      ),
    },
    {
      title: '',
      width: '10%',
      render: (_: string, row: TableRow) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => deleteRow(row.productId)}
          disabled={Boolean(serviceId)}
        />
      ),
    },
  ];

  const handleProductModalOpen = () => {
    setIsProductModalOpen(true);
  };

  const handleProductModalClose = () => {
    setIsProductModalOpen(false);
  };

  const handleSaveChanges = async () => {
    try {
      const hasEmptyWarehouse = rows.some((row) => !row.warehouseId);

      if (hasEmptyWarehouse) {
        return showNotification('Выберите склад у всех товаров');
      }

      const hasInvalidAmount = rows.some((row) => {
        const value = Number(row.amount);
        return (
          row.amount === null || row.amount === undefined || Number.isNaN(value)
        );
      });

      if (hasInvalidAmount) {
        return showNotification(
          'В одном или нескольких товарах указано некорректное количество'
        );
      }

      if (rows.length) {
        await saveClientServiceProductUsages({
          ActiveBranchId,
          data: rows.map(({ id, warehouseId, productId, amount }) => {
            return {
              Id: copyingService ? 0 : id,
              ClientServiceId: memoizedServiceId,
              ClientService: null,
              WarehouseId: warehouseId!,
              Warehouse: null,
              ProductId: productId,
              Product: null,
              UsageAmount: amount,
            };
          }),
        }).unwrap();
      } else {
        await DeleteClientServiceProductUsages({
          ActiveBranchId,
          clientServiceId: memoizedServiceId,
        });
      }

      if (copyingService) {
        searchParams.delete('copyingService');
        setSearchParams(searchParams);
      }

      showNotification('Технологическая карта сохранена успешно', SUCCESS);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  const handleCopyButtonClick = () => {
    setIsCopyModalOpen(true);
  };

  return {
    rows,
    columns,
    isProductModalOpen,
    handleProductModalOpen,
    handleProductModalClose,
    onAdd,
    selectedProducts,
    warehousesOptions,
    handleSaveChanges,
    isSaveClientServiceProductUsagesLoading,
    handleCopyButtonClick,
    isCopyModalOpen,
    setIsCopyModalOpen,
    isDeleteClientServiceProductUsagesLoading,
  };
};
